import { Component, OnInit, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Subject, Observable } from 'rxjs';
import * as _ from 'underscore';
import { RootStoreState } from 'src/app/root-store';
import { takeUntil } from 'rxjs/operators';
import { ColumnConfigItem } from 'src/app/models/ColumnConfigItem';

import { getCurrentUser } from 'src/app/root-store/main-app-store/selectors/auth.selector';
import { Rental } from 'src/app/models/Rental.model';
import { ClearRentalList, GetRentalList, GetRentalOption, GetRentalPeriod, GetRentalSize } from 'src/app/root-store/kwikspace-app-store/actions/rental.action';
import { getRentalOptions, getRentalOptionsError, getRentalOptionsLoading, getRentalPeriods, getRentalPeriodsError, getRentalPeriodsLoading, getRentalPriceList, getRentalPriceListError, getRentalPriceListLoading, getRentalSizes, getRentalSizesError, getRentalSizesLoading } from 'src/app/root-store/kwikspace-app-store/selectors/rental.selector';
import { PaginationService } from 'src/app/services/pagination.service';

@Component({
  selector: 'kwikspace-rentals',
  templateUrl: './rentals.component.html',
  styleUrls: ['./rentals.component.scss']
})
export class RentalsComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();

  rentalsLoading$: Observable<any> = this.store.select(getRentalPriceListLoading);
  rentalsError$: Observable<any> = this.store.select(getRentalPriceListError);

  rentalOptionsLoading$: Observable<any> = this.store.select(getRentalOptionsLoading);
  rentalOptionsError$: Observable<any> = this.store.select(getRentalOptionsError);

  rentalSizesLoading$: Observable<any> = this.store.select(getRentalSizesLoading);
  rentalSizesError$: Observable<any> = this.store.select(getRentalSizesError);

  rentalPeriodsLoading$: Observable<any> = this.store.select(getRentalPeriodsLoading);
  rentalPeriodsError$: Observable<any> = this.store.select(getRentalPeriodsError);

  rentals: Rental[];
  pageCount = 1;
  pageLinks;
  itemCount = 0;
  currentPage = 1;
  pageToLoad;

  iconClass: string;
  columnConfig: ColumnConfigItem[];
  filterDropdownOptions: any[];

  filterBy: any;
  sortBy: any;
  searchTerm: any;

  rentalOptions: any[] = [];
  rentalPeriods: any[] = [];
  rentalSizes: any[] = [];

  hasRentals: boolean = false;

  constructor(
    private store: Store<RootStoreState.RootState>,
    private paginationService: PaginationService
  ) { }

  ngOnInit() {
    this.store.dispatch(new GetRentalSize());
    this.store.dispatch(new GetRentalPeriod());
    this.store.dispatch(new GetRentalOption());

    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          this.getRentals();
        }
      });

    this.store
      .select(getRentalPriceList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((rentals: any) => {
        if (rentals) {
          let _rentals = rentals.results ? rentals.results : rentals.length > 0 ? rentals : [];

          this.itemCount = rentals.count;
          this.pageLinks = rentals.links;
          this.pageCount = this.itemCount < 10 ? 1 : Math.ceil(this.itemCount / 10);

          if (_rentals.length > 0) {
            this.searchTerm = '';
            this.filterBy = '';
            this.sortBy = '';
          }

          this.buildRentalTable(_rentals);
        }
      });

    this.store
      .select(getRentalPeriods)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((periods: any) => {
        if (periods) {
          periods.forEach(period => {
            Object.keys(period).forEach(key => {
              if (key == 'period') {
                period.name = period[key];
              }
            })
          });
          this.rentalPeriods = periods;
        }
      });

    this.store
      .select(getRentalOptions)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((options: any) => {
        if (options) {
          options.forEach(option => {
            Object.keys(option).forEach(key => {
              if (key == 'description') {
                option.name = option[key];
              }
            })
          });
          this.rentalOptions = options;
        }
      });

    this.store
      .select(getRentalSizes)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((sizes: any) => {
        if (sizes) {
          sizes.forEach(size => {
            Object.keys(size).forEach(key => {
              if (key == 'size') {
                size.name = size[key];
              }
            })
          });
          this.rentalSizes = sizes;
        }
      });

    this.iconClass = 'fa fa-user text-white';

    this.columnConfig = [
      {
        oldName: 'id',
        isSortable: false,
        isVisible: false
      }
    ];
  }

  buildRentalTable(rentals) {
    if (rentals.length > 0) {
      rentals.forEach(rental => {
        Object.keys(rental).forEach(key => {
          let obj = rental[key];

          if (key == 'category') {
            rental[key] = obj.name;
          }
          if (key == 'item') {
            rental[key] = obj.description;
          }
          if (key == 'size') {
            rental[key] = obj.size;
          }
          if (key == 'period') {
            rental[key] = obj.period;
          }
        });
      });
    }

    this.getFilterOptions();

    this.rentals = rentals;
    this.hasRentals = this.rentals ? true : false;
  }

  getFilterOptions() {
    this.filterDropdownOptions = [
      {
        id: 'clear',
        key: 'Clear',
        filters: [
          {
            id: 'clear',
            name: 'Clear Filters'
          }
        ]
      },
      {
        id: 'period',
        key: 'Period',
        filters: this.rentalPeriods
      },
      {
        id: 'size',
        key: 'Size',
        filters: this.rentalSizes
      },
      {
        id: 'item',
        key: 'Item',
        filters: this.rentalOptions
      },
    ]
  }

  getRentals() {
    this._reloadList();
  }

  viewItem(rentalDetails: any) {
    // get by id
    // # routing
  }

  retry() {
    this.getRentals();
  }

  sort(sortableOptions: any) {
    this.currentPage = 1;

    this.sortBy = sortableOptions;
    this._reloadList();
  }

  search(searchTerm: string) {
    this.currentPage = 1;

    this.searchTerm = searchTerm;
    this._reloadList();
  }

  filter(filterBy: object) {
    this.currentPage = 1;
    this.searchTerm = '';
    this.sortBy = '';

    this.filterBy = filterBy;
    this._reloadList();
  }

  paginate(page: string) {
    let pageProps: any = this.paginationService.getPageProps(page, this.pageCount, this.pageLinks);

    this.currentPage = pageProps.current;
    this.pageToLoad = pageProps.pageToLoad;

    this._reloadList();
  }

  private _reloadList() {
    this.store.dispatch(new ClearRentalList());
    this._loadList();
  }

  _loadList() {
    this.store.dispatch(
      new GetRentalList({
        search: this.searchTerm,
        filter: this.filterBy,
        sort: this.sortBy,
        page: this.pageToLoad,
      })
    );
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

}
